import { useState, useEffect, useRef } from 'react';
import api, { appointmentsAPI, holidaysAPI, customersAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Calendar as SmallCalendar } from '@/components/ui/calendar';
import AddressAutocomplete from '@/components/AddressAutocomplete';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { 
  Calendar as CalendarIcon, 
  Clock,
  X,
  Lock,
  User,
  Phone,
  Mail,
  MapPin,
  FileText,
  ChevronLeft,
  ChevronRight,
  Plus,
  Trash2,
  Edit,
  Save,
  Search,
  CheckCircle,
  Send,
  Loader2,
  KeyRound,
  Copy,
  Check
} from 'lucide-react';
import { format, addDays, addWeeks, addMonths, startOfWeek, endOfWeek, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isSameMonth, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const CopyBtn = ({ value, label }) => {
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  return (
    <button
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          setCopied(true);
          toast.success(`${label} kopiert`);
          setTimeout(() => setCopied(false), 1500);
        } catch { toast.error('Kopieren fehlgeschlagen'); }
      }}
      className={`p-1.5 rounded-md border flex-shrink-0 transition-colors ${copied ? 'bg-green-50 border-green-300' : 'bg-white border-gray-200 hover:bg-gray-50 hover:border-gray-300'}`}
      data-testid={`copy-appt-${label.toLowerCase()}`}
      title={`${label} kopieren`}
    >
      {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-gray-500" />}
    </button>
  );
};

// Appointment category colors
const getCategoryColor = (type) => {
  switch(type) {
    case 'besichtigung': return { bg: '#FF9500', text: 'white', accent: '#FFD699' }; // Orange
    case 'beratung': return { bg: '#007AFF', text: 'white', accent: '#99CAFF' };     // Blue
    case 'aufmass': return { bg: '#5856D6', text: 'white', accent: '#A5A4E3' };      // Indigo
    case 'baustelle': return { bg: '#34C759', text: 'white', accent: '#99E6AA' };    // GREEN ✓
    case 'rueckruf': return { bg: '#FF3B30', text: 'white', accent: '#FF9E99' };     // Red
    case 'privat': return { bg: '#8E8E93', text: 'white', accent: '#C7C7CC' };       // Gray
    default: return { bg: '#9B1B30', text: 'white', accent: '#FFB088' };              // Wine Red
  }
};

const getCategoryLabel = (type) => {
  switch(type) {
    case 'besichtigung': return '🏠 Besichtigung';
    case 'beratung': return '💬 Beratung';
    case 'aufmass': return '📐 Aufmaß';
    case 'baustelle': return '🔨 Baustelle';
    case 'rueckruf': return '📞 Rückruf';
    case 'privat': return '🔒 Privat';
    default: return '📅 Termin';
  }
};

// German public holidays (bundesweite + NRW Feiertage + Karneval)
const getGermanHolidays = (year) => {
  // Calculate Easter Sunday (Gauss algorithm)
  const getEasterSunday = (year) => {
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    const day = ((h + l - 7 * m + 114) % 31) + 1;
    return new Date(year, month - 1, day);
  };

  const easter = getEasterSunday(year);
  const addDaysToDate = (date, days) => {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  };

  const holidays = [
    // ===== BUNDESWEITE FEIERTAGE =====
    { date: new Date(year, 0, 1), name: 'Neujahr' },
    { date: new Date(year, 4, 1), name: 'Tag der Arbeit' },
    { date: new Date(year, 9, 3), name: 'Tag der Deutschen Einheit' },
    { date: new Date(year, 11, 24), name: 'Heiligabend' },
    { date: new Date(year, 11, 25), name: '1. Weihnachtstag' },
    { date: new Date(year, 11, 26), name: '2. Weihnachtstag' },
    { date: new Date(year, 11, 31), name: 'Silvester' },
    
    // Bewegliche Feiertage (abhängig von Ostern)
    { date: addDaysToDate(easter, -2), name: 'Karfreitag' },
    { date: easter, name: 'Ostersonntag' },
    { date: addDaysToDate(easter, 1), name: 'Ostermontag' },
    { date: addDaysToDate(easter, 39), name: 'Christi Himmelfahrt' },
    { date: addDaysToDate(easter, 49), name: 'Pfingstsonntag' },
    { date: addDaysToDate(easter, 50), name: 'Pfingstmontag' },
    
    // ===== NRW-SPEZIFISCHE FEIERTAGE =====
    { date: addDaysToDate(easter, 60), name: 'Fronleichnam' },
    { date: new Date(year, 10, 1), name: 'Allerheiligen' },
    
    // ===== KARNEVAL (NRW/Rheinland) =====
    { date: addDaysToDate(easter, -52), name: 'Weiberfastnacht' },
    { date: addDaysToDate(easter, -48), name: 'Rosenmontag' },
    { date: addDaysToDate(easter, -47), name: 'Karnevalsdienstag' },
  ];

  return holidays;
};

// Check if date is a Sunday (Sonntagsruhe - no construction work allowed)
const isSunday = (date) => {
  return date.getDay() === 0;
};

// Check if a date is a German holiday
const isGermanHoliday = (date, holidayList) => {
  return holidayList.find(h => 
    h.date.getFullYear() === date.getFullYear() &&
    h.date.getMonth() === date.getMonth() &&
    h.date.getDate() === date.getDate()
  );
};

export default function AdminAppointments() {
  const calendarRef = useRef(null);
  const [appointments, setAppointments] = useState([]);
  const [blockedDates, setBlockedDates] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [germanHolidays, setGermanHolidays] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('timeGridWeek');
  const [mobileViewMode, setMobileViewMode] = useState('list'); // 'list', 'day', 'week', 'month'
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 1024);
  
  // Load German holidays for current and next year
  useEffect(() => {
    const currentYear = new Date().getFullYear();
    const allHolidays = [
      ...getGermanHolidays(currentYear),
      ...getGermanHolidays(currentYear + 1)
    ];
    setGermanHolidays(allHolidays);
  }, []);
  
  // Touch/Swipe handling
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  
  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };
  
  const handleTouchEnd = () => {
    const diff = touchStartX.current - touchEndX.current;
    const minSwipeDistance = 50;
    
    if (Math.abs(diff) > minSwipeDistance) {
      if (diff > 0) {
        // Swipe left - next
        navigateCalendar('next');
      } else {
        // Swipe right - previous
        navigateCalendar('prev');
      }
    }
  };
  
  // Check screen size for mobile view
  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Dialogs
  const [showBlockDialog, setShowBlockDialog] = useState(false);
  const [selectedBlockDate, setSelectedBlockDate] = useState(null);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  
  // Form data for creating/editing appointments
  const [appointmentForm, setAppointmentForm] = useState({
    customer_id: '',
    date: '',
    time: '15:00',
    duration: 15,
    description: '',
    appointment_type: 'besichtigung',
    end_date: '',
    end_time: '',
  });

  // New customer inline form
  const [newCustomerForm, setNewCustomerForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    street: '',
    postal_code: '',
    city: '',
  });

  // Toggle between existing and new customer
  const [customerMode, setCustomerMode] = useState('existing'); // 'existing', 'new', 'none'
  
  // Customer search
  const [customerSearch, setCustomerSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [sendingArrivalNotice, setSendingArrivalNotice] = useState(false);
  const [sendingQuickEmail, setSendingQuickEmail] = useState('');
  const searchRef = useRef(null);

  // Appointment type colors and labels
  const appointmentTypes = {
    besichtigung: { label: 'Besichtigung', color: '#F59E0B', icon: '🔶' },
    baustelle: { label: 'Beginn Baustelle', color: '#10B981', icon: '🟢' },
    rueckruf: { label: 'Rückruf', color: '#EF4444', icon: '🔴' },
    privat: { label: 'Privater Termin', color: '#3B82F6', icon: '🔵' },
  };

  useEffect(() => {
    fetchData();
    fetchHolidays();
    fetchCustomers();
  }, []);

  const fetchData = async () => {
    try {
      const [aptRes, blockedRes] = await Promise.all([
        appointmentsAPI.getAll(),
        appointmentsAPI.getBlockedDates(),
      ]);
      setAppointments(aptRes.data);
      setBlockedDates(blockedRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Fehler beim Laden der Termine');
    } finally {
      setLoading(false);
    }
  };

  const fetchHolidays = async () => {
    try {
      const currentYear = new Date().getFullYear();
      const [h1, h2] = await Promise.all([
        holidaysAPI.getByYear(currentYear),
        holidaysAPI.getByYear(currentYear + 1),
      ]);
      setHolidays([...h1.data, ...h2.data]);
    } catch (error) {
      console.error('Failed to fetch holidays:', error);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await customersAPI.getAll();
      setCustomers(response.data);
    } catch (error) {
      console.error('Failed to fetch customers:', error);
    }
  };

  // Customer search function
  const handleCustomerSearch = (searchTerm) => {
    setCustomerSearch(searchTerm);
    
    if (!searchTerm.trim()) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }
    
    const term = searchTerm.toLowerCase();
    const results = customers.filter(c => {
      const searchFields = [
        c.customer_number?.toLowerCase() || '',
        c.first_name?.toLowerCase() || '',
        c.last_name?.toLowerCase() || '',
        c.email?.toLowerCase() || '',
        c.phone?.toLowerCase() || '',
        c.street?.toLowerCase() || '',
        c.postal_code?.toLowerCase() || '',
        c.city?.toLowerCase() || '',
        `${c.first_name} ${c.last_name}`.toLowerCase(),
      ];
      return searchFields.some(field => field.includes(term));
    });
    
    setSearchResults(results.slice(0, 10)); // Limit to 10 results
    setShowSearchResults(true);
  };

  // Select customer from search results
  const handleSelectCustomer = (customer) => {
    setSelectedCustomer(customer);
    setAppointmentForm({ ...appointmentForm, customer_id: customer.id });
    setCustomerSearch(`${customer.customer_number} - ${customer.first_name} ${customer.last_name}`);
    setShowSearchResults(false);
  };

  // Clear selected customer
  const clearSelectedCustomer = () => {
    setSelectedCustomer(null);
    setAppointmentForm({ ...appointmentForm, customer_id: '' });
    setCustomerSearch('');
    setSearchResults([]);
  };

  // Close search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearchResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Cancel appointment (keeps record, marks as cancelled)
  const handleCancelAppointment = async () => {
    if (!selectedAppointment) return;
    
    try {
      await appointmentsAPI.cancel(selectedAppointment.id);
      toast.success('Termin storniert');
      setShowCancelDialog(false);
      setSelectedAppointment(null);
      fetchData();
    } catch (error) {
      console.error('Failed to cancel appointment:', error);
      toast.error('Fehler beim Stornieren');
    }
  };

  // Delete appointment permanently
  const handleDeleteAppointment = async () => {
    if (!selectedAppointment) return;
    
    try {
      await appointmentsAPI.adminDelete(selectedAppointment.id);
      toast.success('Termin gelöscht');
      setShowDeleteDialog(false);
      setSelectedAppointment(null);
      fetchData();
    } catch (error) {
      console.error('Failed to delete appointment:', error);
      toast.error('Fehler beim Löschen');
    }
  };

  // Create new appointment
  const handleCreateAppointment = async () => {
    // Validation: date and time are always required
    if (!appointmentForm.date || !appointmentForm.time) {
      toast.error('Bitte Datum und Uhrzeit ausfüllen');
      return;
    }

    // Validate optional end_date range
    if (appointmentForm.end_date && appointmentForm.end_date < appointmentForm.date) {
      toast.error('Enddatum darf nicht vor dem Startdatum liegen');
      return;
    }

    // Build request payload
    const payload = {
      date: appointmentForm.date,
      time: appointmentForm.time,
      duration: appointmentForm.duration,
      description: appointmentForm.description,
      appointment_type: appointmentForm.appointment_type,
      end_date: appointmentForm.end_date || null,
      end_time: appointmentForm.end_time || null,
    };

    // Add customer based on mode
    if (customerMode === 'existing' && appointmentForm.customer_id) {
      payload.customer_id = appointmentForm.customer_id;
    } else if (customerMode === 'new') {
      // Validate new customer fields
      if (!newCustomerForm.first_name || !newCustomerForm.last_name || !newCustomerForm.email || !newCustomerForm.phone) {
        toast.error('Bitte alle Kundendaten ausfüllen (Vorname, Nachname, E-Mail, Telefon)');
        return;
      }
      payload.new_customer = newCustomerForm;
    }
    // mode === 'none': no customer_id, no new_customer - appointment without customer

    try {
      await appointmentsAPI.adminCreate(payload);
      toast.success('Termin erstellt');
      setShowCreateDialog(false);
      resetForms();
      fetchData();
      fetchCustomers(); // Refresh customers in case a new one was created
    } catch (error) {
      console.error('Failed to create appointment:', error);
      if (error.response?.data?.detail === 'slot_taken') {
        toast.error('Dieser Zeitslot ist bereits vergeben');
      } else {
        toast.error(error.response?.data?.detail || 'Fehler beim Erstellen');
      }
    }
  };

  const resetForms = () => {
    setAppointmentForm({ customer_id: '', date: '', time: '15:00', duration: 15, description: '', appointment_type: 'besichtigung', end_date: '', end_time: '' });
    setNewCustomerForm({ first_name: '', last_name: '', email: '', phone: '', street: '', postal_code: '', city: '' });
    setCustomerMode('existing');
    setCustomerSearch('');
    setSelectedCustomer(null);
    setSearchResults([]);
    setShowSearchResults(false);
  };

  // Update/Reschedule appointment
  const handleUpdateAppointment = async () => {
    if (!selectedAppointment) return;

    try {
      // Validate optional end_date range
      if (appointmentForm.end_date && appointmentForm.date && appointmentForm.end_date < appointmentForm.date) {
        toast.error('Enddatum darf nicht vor dem Startdatum liegen');
        return;
      }
      await appointmentsAPI.adminUpdate(selectedAppointment.id, {
        date: appointmentForm.date || undefined,
        time: appointmentForm.time || undefined,
        duration: appointmentForm.duration || undefined,
        description: appointmentForm.description,
        // Send as empty string to clear on the backend
        end_date: appointmentForm.end_date === '' ? '' : appointmentForm.end_date,
        end_time: appointmentForm.end_time === '' ? '' : appointmentForm.end_time,
      });
      toast.success('Termin aktualisiert');
      setShowEditDialog(false);
      setSelectedAppointment(null);
      fetchData();
    } catch (error) {
      console.error('Failed to update appointment:', error);
      if (error.response?.data?.detail === 'slot_taken') {
        toast.error('Dieser Zeitslot ist bereits vergeben');
      } else {
        toast.error(error.response?.data?.detail || 'Fehler beim Aktualisieren');
      }
    }
  };

  const handleBlockDate = async () => {
    if (!selectedBlockDate) return;
    
    try {
      await appointmentsAPI.blockDate(format(selectedBlockDate, 'yyyy-MM-dd'));
      toast.success('Datum gesperrt');
      setShowBlockDialog(false);
      setSelectedBlockDate(null);
      fetchData();
    } catch (error) {
      console.error('Failed to block date:', error);
      toast.error('Fehler beim Sperren');
    }
  };

  const handleUnblockDate = async (date) => {
    try {
      await appointmentsAPI.unblockDate(date);
      toast.success('Datum freigegeben');
      fetchData();
    } catch (error) {
      console.error('Failed to unblock date:', error);
      toast.error('Fehler beim Freigeben');
    }
  };

  const handleDateClick = (info) => {
    console.log('dateClick triggered:', info.dateStr);
    const clickedDate = new Date(info.dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (clickedDate >= today) {
      // Extract time from dateStr if it includes time (timeGrid views)
      let clickedTime = '09:00';
      if (info.dateStr.includes('T')) {
        // Format: 2026-01-31T12:00:00
        const timePart = info.dateStr.split('T')[1];
        if (timePart) {
          clickedTime = timePart.substring(0, 5); // Get HH:MM
        }
      }
      
      // Open create dialog with pre-filled date and time
      setAppointmentForm({
        ...appointmentForm,
        date: info.dateStr.split('T')[0],
        time: clickedTime,
      });
      setShowCreateDialog(true);
    }
  };

  // Handle selection in timeGrid view
  const handleSelect = (info) => {
    console.log('select triggered:', info.startStr);
    const selectedDate = new Date(info.startStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (selectedDate >= today) {
      let selectedTime = '09:00';
      if (info.startStr.includes('T')) {
        const timePart = info.startStr.split('T')[1];
        if (timePart) {
          selectedTime = timePart.substring(0, 5);
        }
      }
      
      setAppointmentForm({
        ...appointmentForm,
        date: info.startStr.split('T')[0],
        time: selectedTime,
      });
      setShowCreateDialog(true);
      
      // Clear the selection highlight after opening dialog
      const calendarApi = calendarRef.current?.getApi();
      if (calendarApi) {
        calendarApi.unselect();
      }
    }
  };

  const handleEventClick = (info) => {
    const aptId = info.event.id;
    
    // Check if it's a blocked date
    if (aptId.startsWith('blocked-')) {
      return;
    }
    if (aptId.startsWith('holiday-')) {
      return;
    }
    
    // Handle multi-day event ids like "<uuid>-day3"
    const realId = info.event.extendedProps?.appointmentId || aptId.replace(/-day\d+$/, '');
    const apt = appointments.find(a => a.id === realId);
    if (apt) {
      setSelectedAppointment(apt);
    }
  };

  const openEditDialog = () => {
    if (!selectedAppointment) return;
    setAppointmentForm({
      customer_id: selectedAppointment.customer_id,
      date: selectedAppointment.date,
      time: selectedAppointment.time,
      duration: selectedAppointment.duration,
      description: selectedAppointment.description || '',
      appointment_type: selectedAppointment.appointment_type || 'besichtigung',
      end_date: selectedAppointment.end_date || '',
      end_time: selectedAppointment.end_time || '',
    });
    setShowEditDialog(true);
  };

  const handleSendArrivalNotice = async (minutes = 20) => {
    if (!selectedAppointment) return;
    setSendingArrivalNotice(true);
    try {
      await api.post(`/admin/appointments/${selectedAppointment.id}/send-arrival-notice?minutes=${minutes}`);
      toast.success(`Ankunftsbenachrichtigung gesendet (${minutes} Min.)`);
    } catch (error) {
      const msg = error.response?.data?.detail || 'Fehler beim Senden';
      toast.error(msg);
    } finally {
      setSendingArrivalNotice(false);
    }
  };

  const handleSendQuickEmail = async (templateType) => {
    if (!selectedAppointment) return;
    setSendingQuickEmail(templateType);
    try {
      await api.post(`/admin/appointments/${selectedAppointment.id}/send-quick-email`, { template_type: templateType });
      toast.success('E-Mail erfolgreich gesendet');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'E-Mail konnte nicht gesendet werden');
    } finally {
      setSendingQuickEmail('');
    }
  };

  const navigateCalendar = (direction) => {
    const calendarApi = calendarRef.current?.getApi();
    if (!calendarApi) return;

    if (direction === 'prev') {
      calendarApi.prev();
    } else if (direction === 'next') {
      calendarApi.next();
    } else if (direction === 'today') {
      calendarApi.today();
    }
    setCurrentDate(calendarApi.getDate());
  };

  const changeView = (view) => {
    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi) {
      calendarApi.changeView(view);
      setViewMode(view);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return '#10B981';
      case 'cancelled': return '#EF4444';
      case 'completed': return '#3B82F6';
      default: return '#6B7280';
    }
  };

  const getAppointmentTypeColor = (type) => {
    switch (type) {
      case 'besichtigung': return '#F59E0B';  // Orange
      case 'baustelle': return '#10B981';     // Grün
      case 'rueckruf': return '#EF4444';      // Rot
      case 'privat': return '#3B82F6';        // Blau
      default: return '#F59E0B';
    }
  };

  const getAppointmentTypeIcon = (type) => {
    switch (type) {
      case 'besichtigung': return '🔶';
      case 'baustelle': return '🟢';
      case 'rueckruf': return '🔴';
      case 'privat': return '🔵';
      default: return '📅';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'confirmed': return 'Bestätigt';
      case 'cancelled': return 'Storniert';
      case 'completed': return 'Abgeschlossen';
      default: return status;
    }
  };

  const calendarEvents = [
    ...appointments.flatMap(apt => {
      const aptType = apt.appointment_type || 'besichtigung';
      const color = apt.status === 'cancelled' ? '#9CA3AF' : getAppointmentTypeColor(aptType);
      const icon = getAppointmentTypeIcon(aptType);
      const title = aptType === 'besichtigung' 
        ? `${apt.time} ${apt.customer_name || ''} ${apt.customer_number ? `(${apt.customer_number})` : ''}`
        : `${apt.time} ${icon} ${apt.description || appointmentTypes[aptType]?.label || aptType}`;
      
      // Compute end time (either explicit end_time or from duration)
      const computeEndTime = () => {
        if (apt.end_time) return apt.end_time;
        const [h, m] = apt.time.split(':').map(Number);
        const totalMin = h * 60 + m + (apt.duration || 15);
        return `${Math.floor(totalMin/60).toString().padStart(2,'0')}:${(totalMin%60).toString().padStart(2,'0')}`;
      };
      const dailyEndTime = computeEndTime();
      
      // Multi-day: expand into one event per day (same time-window each day)
      if (apt.end_date && apt.end_date > apt.date) {
        const events = [];
        const start = new Date(apt.date + 'T00:00:00');
        const end = new Date(apt.end_date + 'T00:00:00');
        let dayNum = 1;
        const totalDays = Math.floor((end - start) / 86400000) + 1;
        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
          const iso = `${d.getFullYear()}-${(d.getMonth()+1).toString().padStart(2,'0')}-${d.getDate().toString().padStart(2,'0')}`;
          events.push({
            id: `${apt.id}-day${dayNum}`,
            title: `${apt.time}-${dailyEndTime} ${icon} ${apt.description || appointmentTypes[aptType]?.label || aptType} (Tag ${dayNum}/${totalDays})`,
            start: `${iso}T${apt.time}:00`,
            end: `${iso}T${dailyEndTime}:00`,
            backgroundColor: color,
            borderColor: color,
            className: `appointment-${aptType} appointment-range`,
            extendedProps: { type: 'appointment', status: apt.status, appointmentType: aptType, appointmentId: apt.id, isRange: true, dayOfRange: dayNum, totalDaysOfRange: totalDays }
          });
          dayNum++;
        }
        return events;
      }
      
      // Single day (existing behavior, but honor end_time if given)
      return [{
        id: apt.id,
        title: apt.end_time ? `${apt.time}-${apt.end_time} ${icon} ${apt.description || appointmentTypes[aptType]?.label || aptType}` : title,
        start: `${apt.date}T${apt.time}:00`,
        end: `${apt.date}T${dailyEndTime}:00`,
        backgroundColor: color,
        borderColor: color,
        className: `appointment-${aptType}`,
        extendedProps: { type: 'appointment', status: apt.status, appointmentType: aptType, appointmentId: apt.id }
      }];
    }),
    ...blockedDates.map(blocked => ({
      id: `blocked-${blocked.date}`,
      title: '🔒 Gesperrt',
      start: blocked.date,
      allDay: true,
      backgroundColor: '#FEE2E2',
      borderColor: '#EF4444',
      textColor: '#B91C1C',
      extendedProps: { type: 'blocked', date: blocked.date }
    })),
    ...holidays.map(h => ({
      id: `holiday-${h.date}`,
      title: '🎄 Feiertag',
      start: h.date,
      allDay: true,
      backgroundColor: '#FEF3C7',
      borderColor: '#F59E0B',
      textColor: '#92400E',
      extendedProps: { type: 'holiday' }
    })),
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#9B1B30]" />
      </div>
    );
  }

  const activeAppointments = appointments.filter(a => a.status !== 'cancelled');

  // Helper: check if an appointment spans a given day (multi-day support)
  const isAppointmentOnDay = (apt, day) => {
    if (!apt.date) return false;
    const start = parseISO(apt.date);
    const end = apt.end_date ? parseISO(apt.end_date) : start;
    // Normalize day to midnight and compare
    const d = new Date(day);
    d.setHours(0, 0, 0, 0);
    const s = new Date(start); s.setHours(0, 0, 0, 0);
    const e = new Date(end); e.setHours(0, 0, 0, 0);
    return d >= s && d <= e;
  };
  
  // Helper: compute end time (from end_time or duration)
  const computeAptEndTime = (apt) => {
    if (apt.end_time) return apt.end_time.substring(0, 5);
    if (!apt.time) return '';
    const [h, m] = apt.time.split(':').map(Number);
    const totalMin = h * 60 + m + (apt.duration || 15);
    return `${Math.floor(totalMin/60).toString().padStart(2,'0')}:${(totalMin%60).toString().padStart(2,'0')}`;
  };
  
  // Helper: display "08:00 - 16:00" or fall back to start
  const formatAptTimeRange = (apt) => {
    const start = apt.time?.substring(0, 5) || '00:00';
    const end = computeAptEndTime(apt);
    return end ? `${start} – ${end}` : start;
  };
  
  // Helper: number of days in the range
  const aptRangeDays = (apt) => {
    if (!apt.end_date || apt.end_date === apt.date) return 1;
    const s = parseISO(apt.date), e = parseISO(apt.end_date);
    return Math.floor((e - s) / 86400000) + 1;
  };
  
  // Helper: day X of Y for a given date in the range
  const aptDayNumber = (apt, day) => {
    if (!apt.end_date) return 1;
    const s = parseISO(apt.date);
    const d = new Date(day); d.setHours(0,0,0,0);
    const sm = new Date(s); sm.setHours(0,0,0,0);
    return Math.floor((d - sm) / 86400000) + 1;
  };

  return (
    <div data-testid="admin-appointments">
      {/* Header */}
      <div className="flex flex-col gap-4 mb-6">
        <div>
          <h1 className="font-barlow font-bold text-2xl sm:text-3xl text-[#1A1A1A] mb-2">
            Terminkalender
          </h1>
          <p className="font-manrope text-sm text-gray-600">
            {activeAppointments.length} aktive Termine · {blockedDates.length} gesperrte Tage
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <Button 
            className="btn-primary flex-1 sm:flex-none"
            onClick={() => {
              setAppointmentForm({ customer_id: '', date: '', time: '15:00', duration: 15, description: '', appointment_type: 'besichtigung', end_date: '', end_time: '' });
              setShowCreateDialog(true);
            }}
            data-testid="create-appointment-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">Termin anlegen</span>
            <span className="sm:hidden">Neu</span>
          </Button>
          <Button 
            variant="outline"
            className="flex-1 sm:flex-none"
            onClick={() => setShowBlockDialog(true)}
            data-testid="block-date-btn"
          >
            <Lock className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">Tag sperren</span>
            <span className="sm:hidden">Sperren</span>
          </Button>
        </div>
      </div>

      {/* Calendar Controls removed - integrated into new desktop calendar */}

      {/* Mobile List View - Shown on tablets and phones */}
      {isMobileView && (
        <Card className="mb-6" data-testid="mobile-calendar-view">
          <CardContent className="p-0">
            {/* View Mode Buttons */}
            <div className="flex items-center justify-center gap-1 bg-gray-100 p-2 border-b">
              <Button
                variant={mobileViewMode === 'list' ? 'default' : 'ghost'}
                size="sm"
                className={`h-9 px-3 text-sm font-medium ${mobileViewMode === 'list' ? 'bg-[#9B1B30] hover:bg-[#7A1525] text-white' : 'text-gray-600'}`}
                onClick={() => setMobileViewMode('list')}
              >
                Liste
              </Button>
              <Button
                variant={mobileViewMode === 'month' ? 'default' : 'ghost'}
                size="sm"
                className={`h-9 px-3 text-sm font-medium ${mobileViewMode === 'month' ? 'bg-[#9B1B30] hover:bg-[#7A1525] text-white' : 'text-gray-600'}`}
                onClick={() => setMobileViewMode('month')}
              >
                Monat
              </Button>
              <Button
                variant={mobileViewMode === 'week' ? 'default' : 'ghost'}
                size="sm"
                className={`h-9 px-3 text-sm font-medium ${mobileViewMode === 'week' ? 'bg-[#9B1B30] hover:bg-[#7A1525] text-white' : 'text-gray-600'}`}
                onClick={() => setMobileViewMode('week')}
              >
                Woche
              </Button>
              <Button
                variant={mobileViewMode === 'day' ? 'default' : 'ghost'}
                size="sm"
                className={`h-9 px-3 text-sm font-medium ${mobileViewMode === 'day' ? 'bg-[#9B1B30] hover:bg-[#7A1525] text-white' : 'text-gray-600'}`}
                onClick={() => setMobileViewMode('day')}
              >
                Tag
              </Button>
            </div>

            {/* LIST VIEW - Upcoming Appointments Overview */}
            {mobileViewMode === 'list' && (
              <div className="divide-y divide-gray-100">
                {/* Header */}
                <div className="bg-gradient-to-r from-[#9B1B30] to-[#7B1526] text-white p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-barlow font-bold text-xl">Kommende Termine</div>
                      <div className="text-sm opacity-80">Nächste 14 Tage</div>
                    </div>
                    <Button 
                      size="sm" 
                      className="bg-white/20 hover:bg-white/30 text-white"
                      onClick={() => {
                        setAppointmentForm({ 
                          ...appointmentForm, 
                          date: format(new Date(), 'yyyy-MM-dd'),
                          time: '09:00'
                        });
                        setShowCreateDialog(true);
                      }}
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Neu
                    </Button>
                  </div>
                </div>
                
                {/* Appointments grouped by day */}
                <div className="max-h-[65vh] overflow-y-auto bg-gray-50">
                  {(() => {
                    const today = new Date();
                    const next14Days = Array.from({ length: 14 }).map((_, i) => addDays(today, i));
                    
                    // Group appointments by date
                    const appointmentsByDate = next14Days.map(day => {
                      const dayAppts = appointments
                        .filter(a => a.status !== 'cancelled' && isAppointmentOnDay(a, day))
                        .sort((a, b) => a.time.localeCompare(b.time));
                      const dayBlocked = blockedDates.find(b => isSameDay(parseISO(b.date), day));
                      const dayHoliday = holidays.find(h => isSameDay(parseISO(h.date), day));
                      return { day, appointments: dayAppts, blocked: dayBlocked, holiday: dayHoliday };
                    }).filter(d => d.appointments.length > 0 || d.blocked || d.holiday);
                    
                    if (appointmentsByDate.length === 0) {
                      return (
                        <div className="p-10 text-center">
                          <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm">
                            <CalendarIcon className="w-10 h-10 text-gray-300" />
                          </div>
                          <div className="font-medium text-gray-500 text-lg">Keine Termine</div>
                          <div className="text-sm text-gray-400 mt-1">In den nächsten 14 Tagen</div>
                        </div>
                      );
                    }
                    
                    return appointmentsByDate.map(({ day, appointments: dayAppts, blocked, holiday }) => (
                      <div key={day.toISOString()} className="bg-white mb-2">
                        {/* Day Header */}
                        <div 
                          className={`px-4 py-3 border-b flex items-center justify-between ${
                            isSameDay(day, today) ? 'bg-[#9B1B30]/5' : 'bg-gray-50'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-12 h-12 rounded-xl flex flex-col items-center justify-center ${
                              isSameDay(day, today) 
                                ? 'bg-[#9B1B30] text-white' 
                                : 'bg-white border border-gray-200'
                            }`}>
                              <div className="text-xs font-medium opacity-80">
                                {format(day, 'EEE', { locale: de })}
                              </div>
                              <div className="text-lg font-bold leading-none">
                                {format(day, 'd')}
                              </div>
                            </div>
                            <div>
                              <div className={`font-semibold ${isSameDay(day, today) ? 'text-[#9B1B30]' : 'text-gray-900'}`}>
                                {isSameDay(day, today) ? 'Heute' : format(day, 'EEEE', { locale: de })}
                              </div>
                              <div className="text-sm text-gray-500">
                                {format(day, 'd. MMMM', { locale: de })}
                              </div>
                            </div>
                          </div>
                          <Badge variant="secondary" className="text-xs">
                            {dayAppts.length} {dayAppts.length === 1 ? 'Termin' : 'Termine'}
                          </Badge>
                        </div>
                        
                        {/* Day's appointments */}
                        {blocked ? (
                          <div className="p-4 flex items-center gap-3 bg-red-50">
                            <Lock className="w-5 h-5 text-red-600" />
                            <span className="text-red-700 font-medium">Tag gesperrt</span>
                          </div>
                        ) : holiday ? (
                          <div className="p-4 flex items-center gap-3 bg-amber-50">
                            <CalendarIcon className="w-5 h-5 text-amber-600" />
                            <span className="text-amber-700 font-medium">Feiertag - {holiday.name || 'Geschlossen'}</span>
                          </div>
                        ) : (
                          dayAppts.map((appt) => (
                            <button
                              key={appt.id}
                              onClick={() => setSelectedAppointment(appt)}
                              className="w-full px-4 py-3 flex items-center gap-4 hover:bg-gray-50 active:bg-gray-100 transition-colors text-left border-b border-gray-50 last:border-b-0"
                            >
                              {/* Time */}
                              <div className="flex-shrink-0 w-20 text-center">
                                <div className="font-barlow font-bold text-base text-[#9B1B30] leading-tight">
                                  {formatAptTimeRange(appt)}
                                </div>
                                <div className="text-[10px] text-gray-400">
                                  {appt.end_date && appt.end_date !== appt.date
                                    ? `Tag ${aptDayNumber(appt, day)}/${aptRangeDays(appt)}`
                                    : `${appt.duration || 15} Min`}
                                </div>
                              </div>
                              
                              {/* Category Color Bar */}
                              <div 
                                className="w-1.5 self-stretch rounded-full flex-shrink-0"
                                style={{ backgroundColor: getCategoryColor(appt.appointment_type).bg }}
                              />
                              
                              {/* Content */}
                              <div className="flex-1 min-w-0">
                                <div className="font-semibold text-gray-900">
                                  {appt.customer_name || appt.description || 'Termin'}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {getCategoryLabel(appt.appointment_type)}
                                  {appt.end_date && appt.end_date !== appt.date && ` · ${format(parseISO(appt.date), 'dd.MM.', {locale:de})}–${format(parseISO(appt.end_date), 'dd.MM.', {locale:de})}`}
                                </div>
                              </div>
                              
                              <ChevronRight className="w-5 h-5 text-gray-300 flex-shrink-0" />
                            </button>
                          ))
                        )}
                      </div>
                    ));
                  })()}
                </div>
              </div>
            )}

            {/* Header with Navigation - Only for Day view */}
            {mobileViewMode === 'day' && (
            <div 
              className="bg-gradient-to-r from-[#9B1B30] to-[#7B1526] text-white p-4"
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <div className="flex items-center justify-between mb-3">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="text-white hover:bg-white/20 h-12 w-12 p-0"
                  onClick={() => setCurrentDate(addDays(currentDate, -1))}
                >
                  <ChevronLeft className="w-6 h-6" />
                </Button>
                <button 
                  className="text-center flex-1"
                  onClick={() => setCurrentDate(new Date())}
                >
                  <div className="font-barlow font-bold text-xl">
                    {format(currentDate, 'EEEE', { locale: de })}
                  </div>
                  <div className="text-sm opacity-80">
                    {format(currentDate, 'd. MMMM yyyy', { locale: de })}
                  </div>
                </button>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="text-white hover:bg-white/20 h-12 w-12 p-0"
                  onClick={() => setCurrentDate(addDays(currentDate, 1))}
                >
                  <ChevronRight className="w-6 h-6" />
                </Button>
              </div>
            </div>
            )}

            {/* WEEK VIEW - Apple Calendar Style with Time Grid */}
            {mobileViewMode === 'week' && (
              <div 
                className="bg-black min-h-[85vh] flex flex-col"
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
              >
                {/* Header with Month/Year and View Tabs */}
                <div className="flex-shrink-0 px-3 pt-3 pb-2">
                  <div className="flex items-center justify-between mb-2">
                    <button 
                      onClick={() => setCurrentDate(addWeeks(currentDate, -1))}
                      className="text-white hover:text-[#9B1B30] p-1"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <button 
                      onClick={() => setCurrentDate(new Date())}
                      className="text-center"
                    >
                      <span className="text-white text-lg font-bold">
                        {format(currentDate, 'M/yyyy')}
                      </span>
                    </button>
                    <button 
                      onClick={() => setCurrentDate(addWeeks(currentDate, 1))}
                      className="text-white hover:text-[#9B1B30] p-1"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </div>
                  
                  {/* Day Headers Row - Date numbers with weekday */}
                  <div className="grid grid-cols-8 gap-0 text-center">
                    <div className="w-8"></div> {/* Spacer for time column */}
                    {Array.from({ length: 7 }).map((_, i) => {
                      const day = addDays(startOfWeek(currentDate, { weekStartsOn: 1 }), i);
                      const isToday = isSameDay(day, new Date());
                      const isWeekend = i >= 5;
                      const holiday = isGermanHoliday(day, germanHolidays);
                      const sundayNoWork = isSunday(day);
                      const noWorkDay = holiday || sundayNoWork;
                      const dayNames = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
                      return (
                        <button
                          key={i}
                          onClick={() => {
                            setCurrentDate(day);
                            setMobileViewMode('day');
                          }}
                          className={`py-1 ${noWorkDay ? 'bg-red-500/30 rounded' : ''} ${isWeekend ? 'text-red-500' : 'text-gray-400'}`}
                        >
                          <div className={`text-lg font-bold ${
                            noWorkDay ? 'text-red-400' :
                            isToday ? 'text-[#9B1B30]' : 
                            isWeekend ? 'text-red-500' : 'text-white'
                          }`}>
                            {format(day, 'd')}
                          </div>
                          <div className={`text-[10px] ${noWorkDay ? 'text-red-400' : ''}`}>
                            {noWorkDay ? '🚫' : `(${dayNames[i]})`}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Time Grid with Appointments */}
                <div className="flex-1 overflow-y-auto pb-20">
                  <div className="relative">
                    {/* Time slots from 6:00 to 20:00 */}
                    {Array.from({ length: 15 }).map((_, hourIndex) => {
                      const hour = hourIndex + 6; // Start from 6:00
                      return (
                        <div key={hour} className="grid grid-cols-8 gap-0 border-t border-gray-800" style={{ minHeight: '50px' }}>
                          {/* Time label */}
                          <div className="w-8 text-right pr-1 pt-0.5">
                            <span className="text-gray-500 text-[10px]">{hour}</span>
                          </div>
                          
                          {/* Day columns */}
                          {Array.from({ length: 7 }).map((_, dayIndex) => {
                            const day = addDays(startOfWeek(currentDate, { weekStartsOn: 1 }), dayIndex);
                            const holiday = isGermanHoliday(day, germanHolidays);
                            const sundayNoWork = isSunday(day);
                            const noWorkDay = holiday || sundayNoWork;
                            const dayAppts = appointments
                              .filter(a => {
                                if (a.status === 'cancelled') return false;
                                if (!isAppointmentOnDay(a, day)) return false;
                                // Compute hour range this appt covers (start hour → end hour exclusive at top of hour)
                                const startHour = parseInt(a.time?.split(':')[0] || '0');
                                const startMin = parseInt(a.time?.split(':')[1] || '0');
                                let endMinutes;
                                if (a.end_time) {
                                  const [eh, em] = a.end_time.split(':').map(Number);
                                  endMinutes = eh * 60 + em;
                                } else {
                                  endMinutes = startHour * 60 + startMin + (a.duration || 15);
                                }
                                const endHourCeil = Math.ceil(endMinutes / 60);
                                return hour >= startHour && hour < endHourCeil;
                              })
                              .sort((a, b) => a.time.localeCompare(b.time));
                            
                            return (
                              <div 
                                key={dayIndex} 
                                className={`border-l border-gray-800 p-0.5 min-h-[50px] relative ${noWorkDay ? 'bg-red-500/20' : ''}`}
                                onClick={() => {
                                  if (noWorkDay) {
                                    const reason = holiday ? holiday.name : 'Sonntagsruhe';
                                    toast.error(`${reason} - Keine Arbeiten erlaubt!`);
                                    return;
                                  }
                                  if (dayAppts.length === 0) {
                                    setAppointmentForm({
                                      ...appointmentForm,
                                      date: format(day, 'yyyy-MM-dd'),
                                      time: `${hour.toString().padStart(2, '0')}:00`
                                    });
                                    setShowCreateDialog(true);
                                  }
                                }}
                              >
                                {dayAppts.map(appt => {
                                  const colors = getCategoryColor(appt.appointment_type);
                                  const displayName = appt.customer_name 
                                    ? appt.customer_name.split(' ').slice(0, 2).join(' ')
                                    : appt.description?.substring(0, 15) || 'Termin';
                                  const isRange = appt.end_date && appt.end_date !== appt.date;
                                  const startHour = parseInt(appt.time?.split(':')[0] || '0');
                                  const isStartHour = hour === startHour;
                                  return (
                                    <button
                                      key={appt.id}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedAppointment(appt);
                                      }}
                                      className="w-full rounded p-1 text-left mb-0.5"
                                      style={{ backgroundColor: colors.bg, minHeight: '46px' }}
                                    >
                                      {isStartHour ? (
                                        <>
                                          <div className="text-white text-[10px] font-medium leading-tight">
                                            {formatAptTimeRange(appt)}
                                          </div>
                                          <div className="text-white/90 text-[9px] leading-tight truncate">
                                            {isRange ? `${displayName} (${aptDayNumber(appt, day)}/${aptRangeDays(appt)})` : displayName}
                                          </div>
                                        </>
                                      ) : (
                                        <div className="w-full h-full" aria-label={`Fortsetzung von ${displayName}`} />
                                      )}
                                    </button>
                                  );
                                })}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Bottom Navigation Bar - Apple Calendar Style */}
                <div className="fixed bottom-0 left-0 right-0 bg-black border-t border-gray-800 px-2 py-2 flex items-center justify-around z-50">
                  <button 
                    onClick={() => setCurrentDate(new Date())}
                    className="flex flex-col items-center text-gray-500 hover:text-white"
                  >
                    <ChevronLeft className="w-5 h-5" />
                    <span className="text-[10px]">Heute</span>
                  </button>
                  <button 
                    onClick={() => setMobileViewMode('month')}
                    className="flex flex-col items-center text-gray-500 hover:text-white"
                  >
                    <CalendarIcon className="w-5 h-5" />
                    <span className="text-[10px]">Monat</span>
                  </button>
                  <button 
                    className="flex flex-col items-center text-[#9B1B30]"
                  >
                    <CalendarIcon className="w-5 h-5" />
                    <span className="text-[10px]">Woche</span>
                  </button>
                  <button 
                    onClick={() => setMobileViewMode('day')}
                    className="flex flex-col items-center text-gray-500 hover:text-white"
                  >
                    <Clock className="w-5 h-5" />
                    <span className="text-[10px]">Tag</span>
                  </button>
                  <button 
                    onClick={() => {
                      setAppointmentForm({
                        ...appointmentForm,
                        date: format(currentDate, 'yyyy-MM-dd'),
                        time: '09:00'
                      });
                      setShowCreateDialog(true);
                    }}
                    className="flex flex-col items-center text-[#9B1B30]"
                  >
                    <Plus className="w-6 h-6" />
                  </button>
                </div>
              </div>
            )}

            {/* MONTH VIEW */}
            {mobileViewMode === 'month' && (
            <div 
              className="bg-gradient-to-r from-[#9B1B30] to-[#7B1526] text-white p-4"
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <div className="flex items-center justify-between mb-3">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="text-white hover:bg-white/20 h-12 w-12 p-0"
                  onClick={() => setCurrentDate(addMonths(currentDate, -1))}
                >
                  <ChevronLeft className="w-6 h-6" />
                </Button>
                <button 
                  className="text-center flex-1"
                  onClick={() => setCurrentDate(new Date())}
                >
                  <div className="font-barlow font-bold text-xl">
                    {format(currentDate, 'MMMM yyyy', { locale: de })}
                  </div>
                </button>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="text-white hover:bg-white/20 h-12 w-12 p-0"
                  onClick={() => setCurrentDate(addMonths(currentDate, 1))}
                >
                  <ChevronRight className="w-6 h-6" />
                </Button>
              </div>

              {/* Month Grid */}
              <div 
                className="mt-2"
                onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                >
                  {/* Weekday headers */}
                  <div className="grid grid-cols-7 gap-1 text-center mb-2">
                    {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map(d => (
                      <div key={d} className="text-xs opacity-60 font-medium py-1">{d}</div>
                    ))}
                  </div>
                  {/* Days grid */}
                  <div className="grid grid-cols-7 gap-1 text-center">
                    {(() => {
                      const monthStart = startOfMonth(currentDate);
                      const monthEnd = endOfMonth(currentDate);
                      const startDate = startOfWeek(monthStart, { weekStartsOn: 1 });
                      const endDate = endOfWeek(monthEnd, { weekStartsOn: 1 });
                      const days = eachDayOfInterval({ start: startDate, end: endDate });
                      
                      return days.map((day, i) => {
                        const isToday = isSameDay(day, new Date());
                        const isCurrentMonth = isSameMonth(day, currentDate);
                        const holiday = isGermanHoliday(day, germanHolidays);
                        const sundayNoWork = isSunday(day);
                        const noWorkDay = holiday || sundayNoWork;
                        const dayAppointments = appointments.filter(a => 
                          a.status !== 'cancelled' && 
                          isAppointmentOnDay(a, day)
                        );
                        return (
                          <button
                            key={i}
                            onClick={() => {
                              setCurrentDate(day);
                              setMobileViewMode('day');
                            }}
                            className={`py-2 px-1 rounded-lg transition-all relative ${
                              noWorkDay
                                ? 'bg-red-500/40 text-red-200'
                                : isSameDay(day, currentDate) 
                                  ? 'bg-white text-[#9B1B30] font-bold' 
                                  : isToday 
                                    ? 'bg-white/20' 
                                    : isCurrentMonth
                                      ? 'hover:bg-white/10'
                                      : 'opacity-30'
                            }`}
                            title={holiday ? holiday.name : (sundayNoWork ? 'Sonntagsruhe' : undefined)}
                          >
                            <div className={`text-sm font-medium ${noWorkDay ? 'text-red-300' : ''}`}>
                              {format(day, 'd')}
                            </div>
                            {noWorkDay && (
                              <div className="text-[8px] text-red-300">🚫</div>
                            )}
                            {!noWorkDay && dayAppointments.length > 0 && (
                              <div className={`absolute bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full ${
                                isSameDay(day, currentDate) ? 'bg-[#9B1B30]' : 'bg-white'
                              }`} />
                            )}
                          </button>
                        );
                      });
                    })()}
                  </div>
                </div>
            </div>
            )}
            
            {/* Day's Appointments List - Only for Day and Month views */}
            {(mobileViewMode === 'day' || mobileViewMode === 'month') && (
            <div 
              className="divide-y divide-gray-100"
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <div className="bg-gray-50 px-4 py-3 border-b flex items-center justify-between">
                <div>
                  <div className="font-barlow font-bold text-gray-900 text-lg">
                    {format(currentDate, 'EEEE, d. MMMM', { locale: de })}
                  </div>
                  <div className="text-sm text-gray-500">
                    {appointments.filter(a => 
                      a.status !== 'cancelled' && 
                      isAppointmentOnDay(a, currentDate)
                    ).length} Termine
                  </div>
                </div>
                <Button 
                  size="sm" 
                  className="btn-primary"
                  onClick={() => {
                    setAppointmentForm({ 
                      ...appointmentForm, 
                      date: format(currentDate, 'yyyy-MM-dd'),
                      time: '09:00'
                    });
                    setShowCreateDialog(true);
                  }}
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              
              {/* Appointments List */}
              <div className="max-h-[45vh] overflow-y-auto">
                {(() => {
                  const dayAppts = appointments
                    .filter(a => a.status !== 'cancelled' && isAppointmentOnDay(a, currentDate))
                    .sort((a, b) => a.time.localeCompare(b.time));
                  
                  const dayBlocked = blockedDates.find(b => isSameDay(parseISO(b.date), currentDate));
                  const dayHoliday = holidays.find(h => isSameDay(parseISO(h.date), currentDate));
                  
                  if (dayBlocked) {
                    return (
                      <div className="p-8 text-center">
                        <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                          <Lock className="w-10 h-10 text-red-600" />
                        </div>
                        <div className="font-bold text-red-700 text-lg mb-1">Tag gesperrt</div>
                        <div className="text-gray-500">Keine Termine möglich</div>
                      </div>
                    );
                  }
                  
                  if (dayHoliday) {
                    return (
                      <div className="p-8 text-center">
                        <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                          <CalendarIcon className="w-10 h-10 text-amber-600" />
                        </div>
                        <div className="font-bold text-amber-700 text-lg mb-1">Feiertag</div>
                        <div className="text-gray-500">{dayHoliday.name || 'Geschlossen'}</div>
                      </div>
                    );
                  }
                  
                  if (dayAppts.length === 0) {
                    return (
                      <div className="p-10 text-center">
                        <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                          <CalendarIcon className="w-10 h-10 text-gray-400" />
                        </div>
                        <div className="font-medium text-gray-500 text-lg">Keine Termine</div>
                        <div className="text-sm text-gray-400 mt-1">Tippen Sie +, um einen Termin anzulegen</div>
                      </div>
                    );
                  }
                  
                  return dayAppts.map((appt) => (
                    <button
                      key={appt.id}
                      onClick={() => setSelectedAppointment(appt)}
                      className="w-full p-4 flex items-center gap-4 hover:bg-gray-50 active:bg-gray-100 transition-colors text-left border-b border-gray-50"
                    >
                      {/* Time */}
                      <div className="flex-shrink-0 w-20 text-center">
                        <div className="font-barlow font-bold text-base text-[#9B1B30] leading-tight">
                          {formatAptTimeRange(appt)}
                        </div>
                        <div className="text-[10px] text-gray-400 mt-0.5">
                          {appt.end_date && appt.end_date !== appt.date
                            ? `Tag ${aptDayNumber(appt, currentDate)}/${aptRangeDays(appt)}`
                            : `${appt.duration || 15} Min`}
                        </div>
                      </div>
                      
                      {/* Status Bar */}
                      <div className={`w-1.5 self-stretch rounded-full flex-shrink-0 ${
                        appt.status === 'confirmed' ? 'bg-emerald-500' :
                        appt.status === 'cancelled' ? 'bg-red-500' :
                        'bg-blue-500'
                      }`} />
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-gray-900 text-base">
                          {appt.customer_name || 'Unbekannter Kunde'}
                        </div>
                        <div className="text-sm text-gray-500 mt-0.5">
                          {appt.appointment_type === 'besichtigung' ? '🏠 Besichtigung' :
                           appt.appointment_type === 'beratung' ? '💬 Beratung' :
                           appt.appointment_type === 'aufmass' ? '📐 Aufmaß' : appt.appointment_type}
                        </div>
                        {appt.description && (
                          <div className="text-xs text-gray-400 mt-1 line-clamp-1">
                            {appt.description}
                          </div>
                        )}
                      </div>
                      
                      {/* Arrow */}
                      <ChevronRight className="w-5 h-5 text-gray-300 flex-shrink-0" />
                    </button>
                  ));
                })()}
              </div>
            </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Desktop Calendar - Apple Calendar Style with Time Grid */}
      {!isMobileView && (
      <Card className="mb-6 overflow-hidden" data-testid="calendar-view">
        <CardContent className="p-0">
          <div className="bg-[#1c1c1e] min-h-[700px]">
            {/* Header with Month/Year and Navigation */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800">
              <div className="flex items-center gap-4">
                <button 
                  onClick={() => setCurrentDate(addWeeks(currentDate, -1))}
                  className="text-white hover:text-[#9B1B30] p-2 rounded-lg hover:bg-white/10 transition-colors"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <button 
                  onClick={() => setCurrentDate(new Date())}
                  className="text-center"
                >
                  <span className="text-white text-2xl font-bold">
                    {format(currentDate, 'MMMM yyyy', { locale: de })}
                  </span>
                </button>
                <button 
                  onClick={() => setCurrentDate(addWeeks(currentDate, 1))}
                  className="text-white hover:text-[#9B1B30] p-2 rounded-lg hover:bg-white/10 transition-colors"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
              </div>
              
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => setCurrentDate(new Date())}
                  variant="outline"
                  className="bg-transparent border-gray-600 text-white hover:bg-white/10"
                >
                  Heute
                </Button>
                <Button
                  onClick={() => {
                    setAppointmentForm({
                      ...appointmentForm,
                      date: format(currentDate, 'yyyy-MM-dd'),
                      time: '09:00'
                    });
                    setShowCreateDialog(true);
                  }}
                  className="bg-[#9B1B30] hover:bg-[#7a1526]"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Termin anlegen
                </Button>
              </div>
            </div>
            
            {/* Day Headers Row */}
            <div className="grid grid-cols-8 border-b border-gray-800">
              <div className="w-16 bg-[#2c2c2e]"></div>
              {Array.from({ length: 7 }).map((_, i) => {
                const day = addDays(startOfWeek(currentDate, { weekStartsOn: 1 }), i);
                const isToday = isSameDay(day, new Date());
                const isWeekend = i >= 5;
                const holiday = isGermanHoliday(day, germanHolidays);
                const sundayNoWork = isSunday(day);
                const noWorkDay = holiday || sundayNoWork;
                const dayNames = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];
                return (
                  <div
                    key={i}
                    className={`py-4 text-center border-l border-gray-800 ${
                      noWorkDay ? 'bg-red-500/30' : 
                      isToday ? 'bg-[#9B1B30]/20' : ''
                    }`}
                  >
                    <div className={`text-xs uppercase tracking-wide mb-1 ${
                      noWorkDay ? 'text-red-400' :
                      isWeekend ? 'text-red-500' : 'text-gray-500'
                    }`}>
                      {dayNames[i]}
                    </div>
                    <div className={`text-2xl font-bold ${
                      noWorkDay ? 'text-red-400' :
                      isToday ? 'text-[#9B1B30]' : 
                      isWeekend ? 'text-red-500' : 'text-white'
                    }`}>
                      {format(day, 'd')}
                    </div>
                    {holiday && (
                      <div className="text-[10px] text-red-400 mt-1 font-medium">
                        🚫 {holiday.name}
                      </div>
                    )}
                    {sundayNoWork && !holiday && (
                      <div className="text-[10px] text-red-400 mt-1 font-medium">
                        🚫 Sonntagsruhe
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Time Grid with Appointments */}
            <div className="overflow-y-auto" style={{ maxHeight: '600px' }}>
              {/* Time slots from 6:00 to 20:00 */}
              {Array.from({ length: 15 }).map((_, hourIndex) => {
                const hour = hourIndex + 6;
                return (
                  <div key={hour} className="grid grid-cols-8 border-b border-gray-800" style={{ minHeight: '60px' }}>
                    {/* Time label */}
                    <div className="w-16 text-right pr-3 pt-1 bg-[#2c2c2e] border-r border-gray-800">
                      <span className="text-gray-500 text-sm font-medium">{hour}:00</span>
                    </div>
                    
                    {/* Day columns */}
                    {Array.from({ length: 7 }).map((_, dayIndex) => {
                      const day = addDays(startOfWeek(currentDate, { weekStartsOn: 1 }), dayIndex);
                      const isToday = isSameDay(day, new Date());
                      const holiday = isGermanHoliday(day, germanHolidays);
                      const sundayNoWork = isSunday(day);
                      const noWorkDay = holiday || sundayNoWork;
                      const dayAppts = appointments
                        .filter(a => {
                          if (a.status === 'cancelled') return false;
                          if (!isAppointmentOnDay(a, day)) return false;
                          const startHour = parseInt(a.time?.split(':')[0] || '0');
                          const startMin = parseInt(a.time?.split(':')[1] || '0');
                          let endMinutes;
                          if (a.end_time) {
                            const [eh, em] = a.end_time.split(':').map(Number);
                            endMinutes = eh * 60 + em;
                          } else {
                            endMinutes = startHour * 60 + startMin + (a.duration || 15);
                          }
                          const endHourCeil = Math.ceil(endMinutes / 60);
                          return hour >= startHour && hour < endHourCeil;
                        })
                        .sort((a, b) => a.time.localeCompare(b.time));
                      
                      return (
                        <div 
                          key={dayIndex} 
                          className={`border-l border-gray-800 p-1 min-h-[60px] relative cursor-pointer hover:bg-white/5 transition-colors ${
                            noWorkDay ? 'bg-red-500/20' :
                            isToday ? 'bg-[#9B1B30]/10' : ''
                          }`}
                          onClick={() => {
                            if (noWorkDay) {
                              const reason = holiday ? holiday.name : 'Sonntagsruhe';
                              toast.error(`${reason} - Keine Baustellenarbeiten erlaubt!`);
                              return;
                            }
                            if (dayAppts.length === 0) {
                              setAppointmentForm({
                                ...appointmentForm,
                                date: format(day, 'yyyy-MM-dd'),
                                time: `${hour.toString().padStart(2, '0')}:00`
                              });
                              setShowCreateDialog(true);
                            }
                          }}
                        >
                          {dayAppts.map(appt => {
                            const colors = getCategoryColor(appt.appointment_type);
                            const displayName = appt.customer_name 
                              ? appt.customer_name
                              : appt.description?.substring(0, 20) || 'Termin';
                            const isRange = appt.end_date && appt.end_date !== appt.date;
                            const startHour = parseInt(appt.time?.split(':')[0] || '0');
                            const isStartHour = hour === startHour;
                            return (
                              <button
                                key={appt.id}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedAppointment(appt);
                                }}
                                className="w-full rounded-lg p-2 text-left mb-1 hover:opacity-90 transition-opacity"
                                style={{ backgroundColor: colors.bg, minHeight: '56px' }}
                              >
                                {isStartHour ? (
                                  <>
                                    <div className="text-white text-sm font-bold">
                                      {formatAptTimeRange(appt)}
                                    </div>
                                    <div className="text-white/90 text-sm truncate">
                                      {displayName}
                                    </div>
                                    {isRange ? (
                                      <div className="text-white/80 text-[10px] font-medium">
                                        Tag {aptDayNumber(appt, day)}/{aptRangeDays(appt)}
                                      </div>
                                    ) : appt.customer_number && (
                                      <div className="text-white/70 text-xs">
                                        {appt.customer_number}
                                      </div>
                                    )}
                                  </>
                                ) : (
                                  <div className="w-full h-full" aria-label={`Fortsetzung von ${displayName}`} />
                                )}
                              </button>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        </CardContent>
      </Card>
      )}

      {/* Blocked Dates */}
      {blockedDates.length > 0 && (
        <Card className="mb-6" data-testid="blocked-dates-card">
          <CardHeader>
            <CardTitle className="font-barlow text-lg flex items-center gap-2">
              <Lock className="w-5 h-5 text-red-600" />
              Gesperrte Tage ({blockedDates.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {blockedDates.map((blocked) => (
                <Badge key={blocked.date} variant="outline" className="bg-red-50 border-red-200 text-red-700 py-2 px-3">
                  {format(new Date(blocked.date), 'EEEE, dd.MM.yyyy', { locale: de })}
                  <button onClick={() => handleUnblockDate(blocked.date)} className="ml-2 hover:text-red-900">
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Legend */}
      <Card>
        <CardContent className="p-4">
          <p className="font-barlow font-bold text-sm mb-3">Legende</p>
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center gap-2"><div className="w-4 h-4 rounded bg-emerald-500" /><span className="font-manrope text-sm">Bestätigt</span></div>
            <div className="flex items-center gap-2"><div className="w-4 h-4 rounded bg-red-500" /><span className="font-manrope text-sm">Storniert</span></div>
            <div className="flex items-center gap-2"><div className="w-4 h-4 rounded bg-blue-500" /><span className="font-manrope text-sm">Abgeschlossen</span></div>
            <div className="flex items-center gap-2"><div className="w-4 h-4 rounded bg-red-100 border border-red-400" /><span className="font-manrope text-sm">Gesperrt</span></div>
            <div className="flex items-center gap-2"><div className="w-4 h-4 rounded bg-amber-100 border border-amber-400" /><span className="font-manrope text-sm">Feiertag</span></div>
          </div>
        </CardContent>
      </Card>

      {/* Appointment Detail Sheet */}
      <Sheet open={!!selectedAppointment && !showEditDialog} onOpenChange={() => setSelectedAppointment(null)}>
        <SheetContent className="sm:max-w-lg">
          <SheetHeader>
            <SheetTitle className="font-barlow text-xl flex items-center gap-2">
              <CalendarIcon className="w-5 h-5 text-[#9B1B30]" />
              Termindetails
            </SheetTitle>
          </SheetHeader>
          {selectedAppointment && (
            <div className="mt-6 space-y-6">
              <Badge className={
                selectedAppointment.status === 'confirmed' ? 'bg-emerald-100 text-emerald-700 text-base px-4 py-2' :
                selectedAppointment.status === 'cancelled' ? 'bg-red-100 text-red-700 text-base px-4 py-2' :
                'bg-blue-100 text-blue-700 text-base px-4 py-2'
              }>
                {getStatusLabel(selectedAppointment.status)}
              </Badge>

              <div className="bg-[#9B1B30]/5 p-4 rounded-lg">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-[#9B1B30] text-white rounded-lg flex flex-col items-center justify-center">
                    <span className="font-barlow font-bold text-2xl leading-none">
                      {format(new Date(selectedAppointment.date), 'd')}
                    </span>
                    <span className="font-manrope text-xs">
                      {format(new Date(selectedAppointment.date), 'MMM', { locale: de })}
                    </span>
                  </div>
                  <div>
                    <p className="font-barlow font-bold text-lg">{format(new Date(selectedAppointment.date), 'EEEE', { locale: de })}</p>
                    <p className="font-manrope text-gray-600 flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {selectedAppointment.time} Uhr · {selectedAppointment.duration} Min
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <p className="font-barlow font-bold text-sm text-gray-500 uppercase tracking-wider">Kundendaten</p>
                <div className="flex items-center gap-3">
                  <User className="w-5 h-5 text-gray-400" />
                  <div className="flex-1">
                    <p className="font-manrope font-medium">{selectedAppointment.customer_name}</p>
                    <p className="font-barlow font-bold text-[#9B1B30]">{selectedAppointment.customer_number}</p>
                  </div>
                  <CopyBtn value={`${selectedAppointment.customer_name}\n${selectedAppointment.customer_number}`} label="Name" />
                </div>
                {selectedAppointment.customer_phone && (
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-gray-400" />
                    <p className="font-manrope flex-1">{selectedAppointment.customer_phone}</p>
                    <CopyBtn value={selectedAppointment.customer_phone} label="Telefon" />
                  </div>
                )}
                {selectedAppointment.customer_email && (
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-gray-400" />
                    <p className="font-manrope flex-1">{selectedAppointment.customer_email}</p>
                    <CopyBtn value={selectedAppointment.customer_email} label="E-Mail" />
                  </div>
                )}
                {selectedAppointment.customer_address && (
                  <div className="flex items-center gap-3">
                    <MapPin className="w-5 h-5 text-gray-400" />
                    <p className="font-manrope flex-1">{selectedAppointment.customer_address}</p>
                    <CopyBtn value={selectedAppointment.customer_address} label="Adresse" />
                  </div>
                )}
              </div>

              {selectedAppointment.description && (
                <div className="space-y-2">
                  <p className="font-barlow font-bold text-sm text-gray-500 uppercase tracking-wider">Anmerkungen</p>
                  <div className="flex items-start gap-3">
                    <FileText className="w-5 h-5 text-gray-400 mt-0.5" />
                    <p className="font-manrope text-gray-600">{selectedAppointment.description}</p>
                  </div>
                </div>
              )}

              <div className="text-sm text-gray-500 font-manrope">
                Erstellt am {format(new Date(selectedAppointment.created_at), 'dd.MM.yyyy HH:mm', { locale: de })}
              </div>

              {selectedAppointment.status === 'confirmed' && (
                <div className="space-y-3 pt-4 border-t">
                  {/* Arrival Notice Button - visible for all confirmed appointments */}
                  {selectedAppointment.customer_email && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                      <p className="text-sm font-medium text-green-800 mb-2 flex items-center gap-2">
                        <Send className="w-4 h-4" />
                        Ankunft ankündigen
                      </p>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1 border-green-300 text-green-700 hover:bg-green-100"
                          onClick={() => handleSendArrivalNotice(10)}
                          disabled={sendingArrivalNotice}
                          data-testid="send-arrival-10min"
                        >
                          {sendingArrivalNotice ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                          10 Min
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1 border-green-300 text-green-700 hover:bg-green-100"
                          onClick={() => handleSendArrivalNotice(20)}
                          disabled={sendingArrivalNotice}
                          data-testid="send-arrival-20min"
                        >
                          {sendingArrivalNotice ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                          20 Min
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1 border-green-300 text-green-700 hover:bg-green-100"
                          onClick={() => handleSendArrivalNotice(30)}
                          disabled={sendingArrivalNotice}
                          data-testid="send-arrival-30min"
                        >
                          {sendingArrivalNotice ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                          30 Min
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Quick Email Section */}
                  {selectedAppointment.customer_email && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-2">
                      <p className="text-sm font-medium text-blue-800 flex items-center gap-2" data-testid="appointment-quick-email-section">
                        <Mail className="w-4 h-4" />
                        Schnell-E-Mail an Kunden
                      </p>
                      <p className="text-xs text-blue-700 font-medium">Baustelle fertig in:</p>
                      <div className="flex gap-1.5">
                        <Button size="sm" variant="outline" className="flex-1 text-xs border-blue-300 text-blue-700 hover:bg-blue-100"
                          onClick={() => handleSendQuickEmail('fertig_30min')} disabled={!!sendingQuickEmail} data-testid="apt-quick-fertig-30min">
                          {sendingQuickEmail === 'fertig_30min' ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null} 30 Min
                        </Button>
                        <Button size="sm" variant="outline" className="flex-1 text-xs border-blue-300 text-blue-700 hover:bg-blue-100"
                          onClick={() => handleSendQuickEmail('fertig_1stunde')} disabled={!!sendingQuickEmail} data-testid="apt-quick-fertig-1std">
                          {sendingQuickEmail === 'fertig_1stunde' ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null} 1 Std
                        </Button>
                        <Button size="sm" variant="outline" className="flex-1 text-xs border-blue-300 text-blue-700 hover:bg-blue-100"
                          onClick={() => handleSendQuickEmail('fertig_morgen')} disabled={!!sendingQuickEmail} data-testid="apt-quick-fertig-morgen">
                          {sendingQuickEmail === 'fertig_morgen' ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null} Morgen
                        </Button>
                      </div>
                      <p className="text-xs text-blue-700 font-medium mt-1">Schlüssel:</p>
                      <div className="flex gap-1.5">
                        <Button size="sm" variant="outline" className="flex-1 text-xs border-blue-300 text-blue-700 hover:bg-blue-100"
                          onClick={() => handleSendQuickEmail('schluessel_erhalten')} disabled={!!sendingQuickEmail} data-testid="apt-quick-schluessel-erhalten">
                          {sendingQuickEmail === 'schluessel_erhalten' ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <KeyRound className="w-3 h-3 mr-1" />} Erhalten
                        </Button>
                        <Button size="sm" variant="outline" className="flex-1 text-xs border-blue-300 text-blue-700 hover:bg-blue-100"
                          onClick={() => handleSendQuickEmail('schluessel_zurueck')} disabled={!!sendingQuickEmail} data-testid="apt-quick-schluessel-zurueck">
                          {sendingQuickEmail === 'schluessel_zurueck' ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <KeyRound className="w-3 h-3 mr-1" />} Zurückgegeben
                        </Button>
                      </div>
                      <p className="text-xs text-blue-700 font-medium mt-1">Verspätung:</p>
                      <div className="flex gap-1.5">
                        <Button size="sm" variant="outline" className="flex-1 text-xs border-orange-300 text-orange-700 hover:bg-orange-100"
                          onClick={() => handleSendQuickEmail('verspaetung_10min')} disabled={!!sendingQuickEmail} data-testid="apt-quick-verspaetung-10">
                          {sendingQuickEmail === 'verspaetung_10min' ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Clock className="w-3 h-3 mr-1" />} 10 Min
                        </Button>
                        <Button size="sm" variant="outline" className="flex-1 text-xs border-orange-300 text-orange-700 hover:bg-orange-100"
                          onClick={() => handleSendQuickEmail('verspaetung_20min')} disabled={!!sendingQuickEmail} data-testid="apt-quick-verspaetung-20">
                          {sendingQuickEmail === 'verspaetung_20min' ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Clock className="w-3 h-3 mr-1" />} 20 Min
                        </Button>
                        <Button size="sm" variant="outline" className="flex-1 text-xs border-orange-300 text-orange-700 hover:bg-orange-100"
                          onClick={() => handleSendQuickEmail('verspaetung_30min')} disabled={!!sendingQuickEmail} data-testid="apt-quick-verspaetung-30">
                          {sendingQuickEmail === 'verspaetung_30min' ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Clock className="w-3 h-3 mr-1" />} 30 Min
                        </Button>
                      </div>
                    </div>
                  )}

                  <div className="flex gap-3">
                    <Button variant="outline" className="flex-1" onClick={openEditDialog} data-testid="edit-appointment-btn">
                      <Edit className="w-4 h-4 mr-2" />
                      Bearbeiten
                    </Button>
                    <Button variant="outline" className="text-amber-600 border-amber-200 hover:bg-amber-50" onClick={() => setShowCancelDialog(true)}>
                      <X className="w-4 h-4 mr-2" />
                      Stornieren
                    </Button>
                    <Button variant="destructive" onClick={() => setShowDeleteDialog(true)} data-testid="delete-appointment-btn">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
              {selectedAppointment.status === 'cancelled' && (
                <div className="pt-4 border-t">
                  <Button variant="destructive" className="w-full" onClick={() => setShowDeleteDialog(true)}>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Endgültig löschen
                  </Button>
                </div>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Create Appointment Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={(open) => { setShowCreateDialog(open); if (!open) resetForms(); }}>
        <DialogContent className="w-[92vw] max-w-lg max-h-[90vh] overflow-y-auto p-3 sm:p-6">
          <DialogHeader className="pb-2">
            <DialogTitle className="font-barlow text-lg">Termin anlegen</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {/* Appointment Type Selection */}
            <div>
              <Label className="font-barlow font-bold text-xs sm:text-sm">Terminart *</Label>
              <Select 
                value={appointmentForm.appointment_type} 
                onValueChange={(v) => setAppointmentForm({...appointmentForm, appointment_type: v})}
              >
                <SelectTrigger className="mt-1 h-10" data-testid="select-appointment-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="besichtigung">🔶 Besichtigung</SelectItem>
                  <SelectItem value="baustelle">🟢 Beginn Baustelle</SelectItem>
                  <SelectItem value="rueckruf">🔴 Rückruf</SelectItem>
                  <SelectItem value="privat">🔵 Privater Termin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Customer Mode Selection */}
            <div className="space-y-2">
              <Label className="font-barlow font-bold text-xs sm:text-sm">Kunde</Label>
              <div className="grid grid-cols-3 gap-1">
                <Button 
                  type="button"
                  variant={customerMode === 'existing' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setCustomerMode('existing')}
                  className="text-[11px] sm:text-xs px-1.5 h-8"
                >
                  Bestehend
                </Button>
                <Button 
                  type="button"
                  variant={customerMode === 'new' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setCustomerMode('new')}
                  className="text-[11px] sm:text-xs px-1.5 h-8"
                >
                  Neu
                </Button>
                <Button 
                  type="button"
                  variant={customerMode === 'none' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setCustomerMode('none')}
                  className="text-[11px] sm:text-xs px-1.5 h-8"
                >
                  Ohne
                </Button>
              </div>
              
              {/* Existing Customer Selection with Search */}
              {customerMode === 'existing' && (
                <div ref={searchRef} className="relative">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      type="text"
                      placeholder="Kundenname, Nr., E-Mail, Telefon eingeben..."
                      value={customerSearch}
                      onChange={(e) => handleCustomerSearch(e.target.value)}
                      onFocus={() => customerSearch && setShowSearchResults(true)}
                      className={`h-11 pl-10 text-sm ${selectedCustomer ? 'pr-10 bg-green-50 border-green-400 ring-2 ring-green-200' : ''}`}
                      data-testid="customer-search-input"
                    />
                    {selectedCustomer && (
                      <button
                        type="button"
                        onClick={clearSelectedCustomer}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500 transition-colors"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                  
                  {/* Search Results Dropdown - Improved Design */}
                  {showSearchResults && searchResults.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border-2 border-[#9B1B30]/20 rounded-xl shadow-xl max-h-60 overflow-y-auto">
                      <div className="px-3 py-2 bg-gray-50 border-b text-xs text-gray-500 font-medium">
                        {searchResults.length} Kunde{searchResults.length !== 1 ? 'n' : ''} gefunden
                      </div>
                      {searchResults.map((c, index) => (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => handleSelectCustomer(c)}
                          className={`w-full px-3 py-3 text-left hover:bg-[#9B1B30]/5 transition-colors ${index !== searchResults.length - 1 ? 'border-b border-gray-100' : ''}`}
                          data-testid={`customer-result-${c.id}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-full bg-[#9B1B30]/10 flex items-center justify-center">
                                <User className="w-4 h-4 text-[#9B1B30]" />
                              </div>
                              <div>
                                <div className="font-semibold text-gray-900">
                                  {c.first_name} {c.last_name}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {c.email || c.phone || 'Keine Kontaktdaten'}
                                </div>
                              </div>
                            </div>
                            <div className="text-right">
                              <span className="inline-block px-2 py-0.5 bg-[#9B1B30] text-white text-xs font-medium rounded">
                                {c.customer_number}
                              </span>
                              {c.city && (
                                <div className="text-[10px] text-gray-400 mt-1">
                                  📍 {c.city}
                                </div>
                              )}
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                  
                  {/* No Results */}
                  {showSearchResults && customerSearch && searchResults.length === 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border rounded-xl shadow-lg p-4 text-center">
                      <div className="text-gray-400 mb-2">
                        <Search className="w-8 h-8 mx-auto opacity-50" />
                      </div>
                      <div className="text-gray-500 text-sm font-medium">Kein Kunde gefunden</div>
                      <div className="text-gray-400 text-xs mt-1">für "{customerSearch}"</div>
                    </div>
                  )}
                  
                  {/* Selected Customer Info - Card Style */}
                  {selectedCustomer && (
                    <div className="mt-2 p-3 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-300 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center">
                          <User className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1">
                          <div className="font-semibold text-green-800">
                            {selectedCustomer.first_name} {selectedCustomer.last_name}
                          </div>
                          <div className="text-xs text-green-600 flex items-center gap-2">
                            <span className="bg-green-200 px-1.5 py-0.5 rounded">{selectedCustomer.customer_number}</span>
                            {selectedCustomer.phone && <span>📞 {selectedCustomer.phone}</span>}
                          </div>
                        </div>
                        <div className="text-green-500">
                          <CheckCircle className="w-6 h-6" />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* New Customer Inline Form */}
              {customerMode === 'new' && (
                <div className="space-y-2 p-2 bg-gray-50 rounded border">
                  <p className="text-xs text-gray-600 font-medium">Neuer Kunde:</p>
                  <div className="grid grid-cols-2 gap-1.5">
                    <Input
                      value={newCustomerForm.first_name}
                      onChange={(e) => setNewCustomerForm({...newCustomerForm, first_name: e.target.value})}
                      placeholder="Vorname *"
                      className="h-9 text-sm"
                    />
                    <Input
                      value={newCustomerForm.last_name}
                      onChange={(e) => setNewCustomerForm({...newCustomerForm, last_name: e.target.value})}
                      placeholder="Nachname *"
                      className="h-9 text-sm"
                    />
                  </div>
                  <Input
                    type="email"
                    value={newCustomerForm.email}
                    onChange={(e) => setNewCustomerForm({...newCustomerForm, email: e.target.value})}
                    placeholder="E-Mail *"
                    className="h-9 text-sm"
                  />
                  <Input
                    value={newCustomerForm.phone}
                    onChange={(e) => setNewCustomerForm({...newCustomerForm, phone: e.target.value})}
                    placeholder="Telefon *"
                    className="h-9 text-sm"
                  />
                  <AddressAutocomplete
                    value={newCustomerForm.street}
                    onChange={(val) => setNewCustomerForm({...newCustomerForm, street: val})}
                    onAddressSelect={(addr) => {
                      setNewCustomerForm({
                        ...newCustomerForm,
                        street: addr.street,
                        postal_code: addr.postcode || newCustomerForm.postal_code,
                        city: addr.city || newCustomerForm.city
                      });
                    }}
                    placeholder="Straße eingeben..."
                    className="[&_input]:h-9 [&_input]:text-sm [&_input]:bg-white [&_input]:border-gray-300 [&_input]:text-gray-900 [&_input]:placeholder-gray-400"
                  />
                  <div className="grid grid-cols-2 gap-1.5">
                    <Input
                      value={newCustomerForm.postal_code}
                      onChange={(e) => setNewCustomerForm({...newCustomerForm, postal_code: e.target.value})}
                      placeholder="PLZ"
                      className="h-9 text-sm"
                    />
                    <Input
                      value={newCustomerForm.city}
                      onChange={(e) => setNewCustomerForm({...newCustomerForm, city: e.target.value})}
                      placeholder="Stadt"
                      className="h-9 text-sm"
                    />
                  </div>
                </div>
              )}
              
              {/* No Customer Info */}
              {customerMode === 'none' && (
                <p className="text-xs text-gray-500 italic">Ohne Kunde</p>
              )}
            </div>
            
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="font-barlow font-bold text-xs">Datum *</Label>
                <Input
                  type="date"
                  value={appointmentForm.date}
                  onChange={(e) => setAppointmentForm({...appointmentForm, date: e.target.value})}
                  className="mt-0.5 h-9 text-sm"
                  data-testid="input-date"
                />
              </div>
              <div>
                <Label className="font-barlow font-bold text-xs">Uhrzeit *</Label>
                <Input
                  type="time"
                  value={appointmentForm.time}
                  onChange={(e) => setAppointmentForm({...appointmentForm, time: e.target.value})}
                  className="mt-0.5 h-9 text-sm"
                  data-testid="select-time"
                />
              </div>
            </div>
            {/* Optional: Enddatum & Endzeit für mehrtägige Termine */}
            <div className="grid grid-cols-2 gap-2 border-t pt-2">
              <div>
                <Label className="font-barlow font-bold text-xs text-gray-500">Enddatum (optional)</Label>
                <Input
                  type="date"
                  value={appointmentForm.end_date}
                  min={appointmentForm.date || undefined}
                  onChange={(e) => setAppointmentForm({...appointmentForm, end_date: e.target.value})}
                  className="mt-0.5 h-9 text-sm"
                  data-testid="input-end-date"
                />
              </div>
              <div>
                <Label className="font-barlow font-bold text-xs text-gray-500">Endzeit (optional)</Label>
                <Input
                  type="time"
                  value={appointmentForm.end_time}
                  onChange={(e) => setAppointmentForm({...appointmentForm, end_time: e.target.value})}
                  className="mt-0.5 h-9 text-sm"
                  data-testid="input-end-time"
                  placeholder="z.B. 16:00"
                />
              </div>
            </div>
            {(appointmentForm.end_date || appointmentForm.end_time) && (
              <p className="text-[11px] text-[#4a6741] -mt-1">
                {appointmentForm.end_date && appointmentForm.end_date !== appointmentForm.date
                  ? `Mehrtägig: ${appointmentForm.date || '?'} bis ${appointmentForm.end_date}${appointmentForm.time ? `, täglich ${appointmentForm.time}` : ''}${appointmentForm.end_time ? `–${appointmentForm.end_time}` : ''} Uhr`
                  : `Eintägig: ${appointmentForm.date || '?'}${appointmentForm.time ? `, ${appointmentForm.time}` : ''}${appointmentForm.end_time ? `–${appointmentForm.end_time}` : ''} Uhr`}
              </p>
            )}
            <div>
              <Label className="font-barlow font-bold text-xs">Dauer (Min.)</Label>
              <Input
                type="number"
                min="5"
                max="480"
                value={appointmentForm.duration}
                onChange={(e) => setAppointmentForm({...appointmentForm, duration: parseInt(e.target.value) || 15})}
                className="mt-0.5 h-9 text-sm"
                placeholder="15, 30, 60..."
              />
            </div>
            <div>
              <Label className="font-barlow font-bold text-xs">Notiz</Label>
              <Textarea
                value={appointmentForm.description}
                onChange={(e) => setAppointmentForm({...appointmentForm, description: e.target.value})}
                className="mt-0.5 min-h-[60px] text-sm"
                placeholder="Optional..."
                data-testid="input-description"
              />
            </div>
          </div>
          <DialogFooter className="flex-col gap-2 pt-2">
            <Button className="btn-primary w-full h-10" onClick={handleCreateAppointment} data-testid="confirm-create-btn">
              <Save className="w-4 h-4 mr-2" />
              Speichern
            </Button>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)} className="w-full h-9">
              Abbrechen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Appointment Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="w-[92vw] max-w-lg max-h-[90vh] overflow-y-auto p-3 sm:p-6">
          <DialogHeader className="pb-2">
            <DialogTitle className="font-barlow text-lg">Termin bearbeiten</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="font-barlow font-bold text-xs">Datum</Label>
                <Input
                  type="date"
                  value={appointmentForm.date}
                  onChange={(e) => setAppointmentForm({...appointmentForm, date: e.target.value})}
                  className="mt-0.5 h-9 text-sm"
                />
              </div>
              <div>
                <Label className="font-barlow font-bold text-xs">Uhrzeit</Label>
                <Input
                  type="time"
                  value={appointmentForm.time}
                  onChange={(e) => setAppointmentForm({...appointmentForm, time: e.target.value})}
                  className="mt-0.5 h-9 text-sm"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 border-t pt-2">
              <div>
                <Label className="font-barlow font-bold text-xs text-gray-500">Enddatum (optional)</Label>
                <Input
                  type="date"
                  value={appointmentForm.end_date}
                  min={appointmentForm.date || undefined}
                  onChange={(e) => setAppointmentForm({...appointmentForm, end_date: e.target.value})}
                  className="mt-0.5 h-9 text-sm"
                  data-testid="edit-input-end-date"
                />
              </div>
              <div>
                <Label className="font-barlow font-bold text-xs text-gray-500">Endzeit (optional)</Label>
                <Input
                  type="time"
                  value={appointmentForm.end_time}
                  onChange={(e) => setAppointmentForm({...appointmentForm, end_time: e.target.value})}
                  className="mt-0.5 h-9 text-sm"
                  data-testid="edit-input-end-time"
                />
              </div>
            </div>
            <div>
              <Label className="font-barlow font-bold text-xs">Dauer (Min.)</Label>
              <Input
                type="number"
                min="5"
                max="480"
                value={appointmentForm.duration}
                onChange={(e) => setAppointmentForm({...appointmentForm, duration: parseInt(e.target.value) || 15})}
                className="mt-0.5 h-9 text-sm"
              />
            </div>
            <div>
              <Label className="font-barlow font-bold text-xs">Notiz (nur intern)</Label>
              <Textarea
                value={appointmentForm.description}
                onChange={(e) => setAppointmentForm({...appointmentForm, description: e.target.value})}
                placeholder="Nur für Sie sichtbar – wird nicht an Kunden gesendet"
                className="mt-0.5 min-h-[60px] text-sm"
              />
            </div>
          </div>
          <DialogFooter className="flex-col gap-2 pt-2">
            <Button className="btn-primary w-full h-10" onClick={handleUpdateAppointment}>
              <Save className="w-4 h-4 mr-2" />
              Speichern
            </Button>
            <Button variant="outline" onClick={() => setShowEditDialog(false)} className="w-full h-9">
              Abbrechen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Block Date Dialog */}
      <Dialog open={showBlockDialog} onOpenChange={setShowBlockDialog}>
        <DialogContent className="w-[95vw] sm:max-w-md p-4 sm:p-6">
          <DialogHeader className="pb-2">
            <DialogTitle className="font-barlow text-lg sm:text-xl">Tag sperren</DialogTitle>
          </DialogHeader>
          <div className="flex justify-center py-2">
            <SmallCalendar
              mode="single"
              selected={selectedBlockDate}
              onSelect={setSelectedBlockDate}
              locale={de}
              disabled={(date) => {
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                return date < today;
              }}
              className="rounded-md border"
            />
          </div>
          {selectedBlockDate && (
            <p className="text-center font-manrope text-sm">
              Ausgewählt: <strong>{format(selectedBlockDate, 'EEEE, d. MMMM yyyy', { locale: de })}</strong>
            </p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBlockDialog(false)}>Abbrechen</Button>
            <Button className="btn-primary" onClick={handleBlockDate} disabled={!selectedBlockDate}>
              <Lock className="w-4 h-4 mr-2" />
              Sperren
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Confirmation */}
      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-barlow">Termin stornieren?</AlertDialogTitle>
            <AlertDialogDescription className="font-manrope">
              Der Termin wird als storniert markiert. Er bleibt in der Historie sichtbar.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={handleCancelAppointment} className="bg-amber-600 hover:bg-amber-700">
              Stornieren
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-barlow">Termin endgültig löschen?</AlertDialogTitle>
            <AlertDialogDescription className="font-manrope">
              Diese Aktion kann nicht rückgängig gemacht werden. Der Termin wird vollständig aus dem System entfernt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteAppointment} className="bg-red-600 hover:bg-red-700">
              Endgültig löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
